import React, { useContext, useEffect, useState } from "react";
import { ListButton } from "@refinedev/antd";
import { Empty, Flex, Spin, Typography, theme } from "antd";
import { ApiOutlined, LeftOutlined } from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { axiosInstance, filterUrlGenerate } from "@/utilities";
import { API_URL_LINKED_SEARCH } from "@/services/XCONS_ENV";
import { SmartMenuContext } from "@/config/context/smartMenuContext";
import { LinkedProducts } from "./linkedProducts";
import { HistorialDelAsociador } from "./historialDelAsociador";

const { Title, Text } = Typography;

/** Lo que la tabla le pasa al abrirlo: los productos a asociar, en orden. */
export interface EstadoDelAsociador {
  cola?: any[];
  /** En cuál de la cola está: Guardar avanza y Atrás retrocede. */
  indice?: number;
}

/**
 * El asociador en pantalla propia (xcons-docs#609). Antes era un panel lateral al 69% sobre
 * la tabla; ahora tiene su URL, la flecha para volver como el resto de las secciones, y el
 * Historial abajo. La disposición es la misma: el producto del seller a la izquierda y el
 * buscador del catálogo a la derecha.
 *
 * Asocia una cola: la fila que se tocó, o los seleccionados con la acción masiva Asociar. Con
 * más de uno, Guardar pasa al siguiente y recién en el último vuelve a la tabla. La URL lleva
 * el SKU del que se está asociando, así que se puede abrir directo: si no llega desde la
 * tabla, se lo pide.
 */
export const AsociadorPage: React.FC = () => {
  const { id, sku = "" } = useParams<{ id: string; sku: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const estado = (location.state ?? {}) as EstadoDelAsociador;
  const cola = estado.cola ?? [];
  const indice = estado.indice ?? 0;
  const { smartMenuName } = useContext(SmartMenuContext);
  const { token } = theme.useToken();
  // La barra fija de abajo: el asociador pone ahí Volver, Catalogar y Guardar.
  const [pie, setPie] = useState<HTMLDivElement | null>(null);

  // El mismo que usa la tabla: el seller del menú, no el de la URL.
  const sellerCode = encodeURIComponent(
    smartMenuName?.replace(/"/g, "").toLowerCase(),
  );

  const delaCola = cola[indice]?.sku === sku ? cola[indice] : null;
  const [producto, setProducto] = useState<any>(delaCola);
  const [cargando, setCargando] = useState(!delaCola);
  // Cambiarlo vuelve a pedir el Historial: después de desasociar o guardar, el evento nuevo.
  const [recargaDelHistorial, setRecargaDelHistorial] = useState(0);

  // **Siempre se pide de nuevo.** La copia de la cola es la fila como estaba al abrirla, y el
  // navegador la guarda con la página: al recargar después de desasociar mostraba el producto
  // todavía asociado. Sólo sirve para no mostrar el asociador vacío mientras llega.
  useEffect(() => {
    if (!sellerCode || !sku) return;
    if (delaCola) setProducto(delaCola);
    setCargando(!delaCola);
    axiosInstance
      .get(
        `${API_URL_LINKED_SEARCH}/${sellerCode}?page=1&limit=20&${filterUrlGenerate(
          [{ field: "sku", operator: "eq", value: sku }],
        )}`,
      )
      .then(({ data }) =>
        setProducto(
          (data?.data ?? []).find((p: any) => p?.sku === sku) ?? null,
        ),
      )
      .catch(() => setProducto(null))
      .finally(() => setCargando(false));
  }, [sellerCode, sku]);

  /**
   * Volver es volver **a la tabla como estaba**: con sus filtros y su página, que viven en la
   * URL. Moverse por la cola reemplaza la entrada del historial, así que la anterior es la
   * tabla. Si se entró directo, no hay a dónde volver y va a la tabla limpia.
   */
  const volver = () => {
    if (location.key !== "default") navigate(-1);
    else navigate(`/sellers/linked-products/${id}`);
  };

  const irA = (i: number) =>
    navigate(
      `/sellers/linked-products/${id}/${encodeURIComponent(cola[i].sku)}`,
      { replace: true, state: { ...estado, indice: i } },
    );

  const haySiguiente = indice < cola.length - 1;

  return (
    <Flex vertical gap={16}>
      <Flex align="center" gap={12}>
        <ListButton onClick={volver} hideText icon={<LeftOutlined />} />
        {/* El ícono de asociar, el mismo de Productos asociados. */}
        <ApiOutlined style={{ fontSize: 20 }} />
        <Title level={4} style={{ margin: 0 }}>
          Asociar producto
        </Title>
        {producto?.name && (
          <Text type="secondary" ellipsis style={{ fontSize: 16 }}>
            {producto.name}
          </Text>
        )}
      </Flex>

      {cargando ? (
        <Flex justify="center" style={{ padding: 48 }}>
          <Spin />
        </Flex>
      ) : !producto ? (
        <Empty description="No encontramos este producto del seller" />
      ) : (
        <>
          <LinkedProducts
            // Uno nuevo de la cola arranca de cero: búsqueda, selección y edición.
            key={producto.sku}
            sellerCode={sellerCode}
            configData={producto}
            posicion={
              cola.length > 1
                ? { actual: indice + 1, total: cola.length }
                : undefined
            }
            haySiguiente={haySiguiente}
            onAtras={() => (indice > 0 ? irA(indice - 1) : volver())}
            onGuardado={() => (haySiguiente ? irA(indice + 1) : volver())}
            // La API graba el evento después de contestar —el Historial no frena la operación, y
            // los datos del producto se guardan como un import que se procesa aparte—: pedirlo
            // en el mismo instante llega antes que él.
            onCambio={() =>
              setTimeout(() => setRecargaDelHistorial((n) => n + 1), 1500)
            }
            contenedorDelPie={pie}
          />
          <HistorialDelAsociador
            producto={producto}
            recarga={recargaDelHistorial}
          />
          {/* Pegada al borde de abajo mientras se scrollea, de lado a lado: se come el
              margen de 24px que el layout le pone al contenido. */}
          <div
            ref={setPie}
            style={{
              position: "sticky",
              bottom: 0,
              zIndex: 10,
              margin: "0 -24px -24px",
              padding: "12px 24px",
              background: token.colorBgLayout,
              borderTop: `1px solid ${token.colorBorderSecondary}`,
            }}
          />
        </>
      )}
    </Flex>
  );
};
